import { BottomStudioBar } from "@/components/flowly/BottomStudioBar/BottomStudioBar";
import { GenerateButton } from "./GenerateButton";
import { BackgroundColorControl } from "./BackgroundColorControl";
import { BarToggle } from "./BarToggle";
import { AspectRatioControl } from "./AspectRatioControl";
import { ResolutionControl } from "./ResolutionControl";
import { QuantityControl } from "./QuantityControl";
import { ModelSelectorControl } from "./ModelSelectorControl";
import { SoundToggleButton } from "@/components/flowly/SoundToggleButton/SoundToggleButton";
import { AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import type { GenerationBarProps } from "./types";

function BarDivider() {
  return <div className="hidden md:block w-px h-5 bg-white/10" />;
}

export function GenerationBar({
  onGenerate,
  generating,
  disabled,
  creditCost,
  costsLoading,
  buttonLabel,
  buttonTooltip,
  selectedModelId,
  onModelChange,
  mediaType,
  models,
  getModelCost,
  aspectRatio,
  onAspectRatioChange,
  availableAspectRatios,
  resolution,
  onResolutionChange,
  availableImageResolutions,
  backgroundColor,
  onBackgroundColorChange,
  videoDuration,
  onVideoDurationChange,
  availableDurations,
  videoResolution,
  onVideoResolutionChange,
  availableResolutions,
  quantity,
  onQuantityChange,
  soundEnabled,
  onSoundToggle,
  alwaysIncludesAudio,
  modelWarning,
  singleModelTooltip,
  onDisabledClick,
}: GenerationBarProps) {
  const { t } = useLanguage();

  return (
    <BottomStudioBar>
      <div className="flex flex-col md:flex-row items-center justify-center gap-3">
        <div className="flex flex-wrap md:flex-nowrap items-center gap-1.5 md:gap-2 w-full md:w-auto justify-center">
          {/* Model group */}
          {selectedModelId && onModelChange && mediaType && (
            <ModelSelectorControl
              selectedModelId={selectedModelId}
              mediaType={mediaType}
              onModelChange={onModelChange}
              models={models}
              getModelCost={getModelCost}
              costsLoading={costsLoading}
              singleModelTooltip={singleModelTooltip}
            />
          )}

          <BarDivider />

          {/* Media settings group */}
          {backgroundColor !== undefined && onBackgroundColorChange && (
            <BackgroundColorControl
              backgroundColor={backgroundColor}
              onBackgroundColorChange={onBackgroundColorChange}
            />
          )}

          {videoDuration !== undefined && onVideoDurationChange && availableDurations && (
            <BarToggle
              value={videoDuration}
              options={availableDurations}
              onChange={onVideoDurationChange}
            />
          )}

          {availableAspectRatios && availableAspectRatios.length > 0 && (
            <AspectRatioControl
              aspectRatio={aspectRatio}
              onAspectRatioChange={onAspectRatioChange}
              availableRatios={availableAspectRatios}
            />
          )}

          {videoResolution !== undefined && onVideoResolutionChange && availableResolutions && (
            <BarToggle
              value={videoResolution}
              options={availableResolutions}
              onChange={onVideoResolutionChange}
            />
          )}

          {resolution !== undefined && onResolutionChange && (
            <ResolutionControl
              resolution={resolution}
              onResolutionChange={onResolutionChange}
              availableResolutions={availableImageResolutions}
            />
          )}

          {/* Extras group */}
          {(soundEnabled !== undefined || alwaysIncludesAudio || quantity !== undefined) && (
            <BarDivider />
          )}

          {soundEnabled !== undefined && onSoundToggle && (
            <SoundToggleButton
              enabled={soundEnabled}
              onToggle={onSoundToggle}
            />
          )}

          {alwaysIncludesAudio && soundEnabled === undefined && (
            <SoundToggleButton enabled onToggle={() => {}} alwaysOn />
          )}

          {quantity !== undefined && onQuantityChange && (
            <QuantityControl
              quantity={quantity}
              onQuantityChange={onQuantityChange}
            />
          )}
        </div>

        <GenerateButton
          onGenerate={onGenerate}
          generating={generating}
          disabled={disabled}
          creditCost={creditCost}
          costsLoading={costsLoading}
          buttonLabel={buttonLabel}
          buttonTooltip={buttonTooltip}
          onDisabledClick={onDisabledClick}
        />
      </div>

      {modelWarning && (
        <div className="flex items-center justify-center gap-1.5 text-amber-500 text-xs mt-1">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{modelWarning}</span>
        </div>
      )}
    </BottomStudioBar>
  );
}
